import {
    BulletList,
    CheckBox,
    Container,
    CopyToClipboard,
    CustomDatePicker,
    cssVar,
    DropDown,
    GenericModal,
    getFormattedCurrency,
    Header,
    Input,
    Label,
    Link,
    SecondaryButton,
    Spinner,
    TextArea,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';

import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { getAndUseRoute } from '../../../../utils';
import ActionsMenuConfig from '../ActionsMenu/configs';

const MenuContainer = styled(Container)`
    z-index: 1;
    margin-top: -25px;
`;

const CustomContainer = styled(Container)`
    min-height: 10rem;
    max-height: 20rem;
    width: 100%;
    padding: 0 1rem;
    border: 1px solid ${cssVar.colorStrokeDark};
    border-radius: 5px;
    overflow-x: hidden;
    overflow-y: auto;
`;

const StyledTextArea = styled(TextArea)`
    border: 1px solid ${cssVar.colorStrokeDark};
    border-radius: ${cssVar.radiusSmall};
    width: 100%;
    box-sizing: border-box;
    //padding: .25rem;
    //height: 150px;
`;

const SpaceBetweenContainer = styled(Container)`
    display: flex;
    justify-content: space-between;
    white-space: normal;
    flex-wrap: wrap;
`;

// The amount field fills the row and stops short of the Apply button.
const RefundOverrideRow = styled(Container)`
    display: flex;
    align-items: center;
    gap: 24px;

    > *:first-child {
        flex: 1;
        min-width: 0;
    }

    > *:last-child {
        flex-shrink: 0;
    }
`;

const ScrollableModalContent = styled.div`
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
`;

const PaymentLinkRow = styled.div`
    display: flex;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    margin-bottom: 1rem;
    border: 1px solid ${cssVar.colorStrokeVeryLight};
    border-radius: ${cssVar.radiusSmall};
    padding: 0.5rem;
`;

const PaymentLinkLabelWrapper = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;

    > div {
        min-width: 0;
        overflow: hidden;
    }

    > div > p {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

// Custom reference serving the PolicyCancellationRefundMethod enum (None is excluded server side).
// Its codes are the enum names, which bind straight back to the enum on the API.
const REFUND_METHOD_REFERENCE = 'CustomReference|Policy|RefundMethod';

const ProfileMenu = (props) => {
    const intl = useIntl();
    const sizes = useSizes();
    const alignments = useAlignments();
    const screen = useScreen();

    const [lostModal, setLostModal] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [rejectReferralModal, setRejectReferralModal] = useState(false);
    const [confirmBindModal, setConfirmBindModal] = useState(false);
    const [issueQuoteModal, setIssueQuoteModal] = useState(false);
    const [issuePolicyModal, setIssuePolicyModal] = useState(false);
    const [shouldProposalEmail, setShouldProposalEmail] = useState(false);
    const [sendPaymentLinkModal, setSendPaymentLinkModal] = useState(false);
    const [paymentLinkConfirmationModal, setPaymentLinkConfirmationModal] = useState(false);
    const [endorseModal, setEndorseModal] = useState(false);
    const [endorsementEffectiveDate, setEndorsementEffectiveDate] = useState(null);
    const [cancelModal, setCancelModal] = useState(false);
    const [cancellationDate, setCancellationDate] = useState(null);
    const [cancellationReason, setCancellationReason] = useState(null);
    const [cancellationOtherReason, setCancellationOtherReason] = useState('');
    const [changeCancellationDateModal, setChangeCancellationDateModal] = useState(false);
    const [newCancellationDate, setNewCancellationDate] = useState(null);
    const [deleteCancellationModal, setDeleteCancellationModal] = useState(false);
    const [refundModal, setRefundModal] = useState(false);
    const [refundOverrideAmount, setRefundOverrideAmount] = useState('');
    const [refundMethod, setRefundMethod] = useState(null);
    const [refundReference, setRefundReference] = useState('');
    const [refundDate, setRefundDate] = useState(null);
    // Tracks an applied override that was not yet completed, so closing the modal can refresh the stale page.
    const [refundOverrideApplied, setRefundOverrideApplied] = useState(false);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [action, setAction] = useState(null);
    const [ccRecipients, setCcRecipients] = useState('');
    const [userAddedNoteForEmail, setUserAddedNoteForEmail] = useState('');
    const [rejectReferralReason, setRejectReferralReason] = useState('');
    const [acceptReferralModal, setAcceptReferralModal] = useState(false);

    const prevIssuePolicyModal = useRef(issuePolicyModal);
    const prevIssueQuoteModal = useRef(issueQuoteModal);
    const prevOverridableErrors = useRef(props.overridableErrors);

    // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on listed deps
    useEffect(() => {
        if (
            (issuePolicyModal && prevIssuePolicyModal.current !== issuePolicyModal) ||
            (issueQuoteModal && prevIssueQuoteModal.current !== issueQuoteModal)
        ) {
            props.document.doQueryShareableDocuments(props.entity);
        }
        prevIssuePolicyModal.current = issuePolicyModal;
        prevIssueQuoteModal.current = issueQuoteModal;
    }, [issuePolicyModal, issueQuoteModal]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on listed deps
    useEffect(() => {
        // Handle Accept Referral after fetching overridable errors
        if (prevOverridableErrors.current !== props.overridableErrors && action?.code === 'AcceptReferral') {
            // If there are errors, show modal
            if (props.overridableErrors && props.overridableErrors.length > 0) {
                setAcceptReferralModal(true);
            } else {
                // No errors - proceed directly with action
                props.doExecutePolicyAction(action);
                setAction(null);
            }
        }
        prevOverridableErrors.current = props.overridableErrors;
    }, [props.overridableErrors]);

    const sortMenuByStatus = (Configs, status, type, role) => {
        const menu = Configs.filter((obj) => {
            if (obj.type === type) {
                return obj.status === status && obj.role === role && obj.type === type;
            }

            return obj.status === status && obj.role === role && obj.type === null;
        });

        if (menu.length > 1) {
            return menu.filter((obj) => obj.type !== null);
        }

        return menu;
    };

    const getAvailableActions = (menu, product, role) => {
        if (menu?.[0]?.actions) {
            return menu[0].actions.filter((actionItem) => {
                let available = true;

                if (
                    (actionItem.applicableProducts && !actionItem.applicableProducts.includes(product)) ||
                    actionItem.excludedProducts?.includes(product) ||
                    actionItem.excludedRoles?.includes(role) ||
                    (actionItem.MGAProducts && !actionItem.MGAProducts.includes(product))
                ) {
                    available = false;
                }
                if (actionItem.code === 'RejectReferral' && props.policySummary.type !== 'certificate') {
                    available = false;
                }
                if (actionItem.code === 'ConfirmBind' && product === 'securemyoffer') {
                    // Hide if the application is a policy and card needs to be charged
                    // Compare with certificate because of other types
                    if (props.policySummary.type !== 'certificate' && props.policy?.allowChargeCardAction) {
                        available = false;
                    }
                }

                return available;
            });
        }

        return null;
    };

    const handleClick = async (actionItem) => {
        // Prevent multiple clicks while any operation is in progress
        if (props.context.loading) {
            return;
        }

        if (actionItem.code === 'Lost') {
            setLostModal(true);
        } else if (actionItem.code === 'RejectSubmission') {
            setRejectModal(true);
        } else if (actionItem.code === 'RejectReferral') {
            setRejectReferralModal(true);
        } else if (actionItem.code === 'Cancel') {
            setCancelModal(true);
        } else if (actionItem.code === 'Refund') {
            props.doReduceCancellationResult(null);
            setRefundOverrideApplied(false);
            props.doGetCancellation({
                policyTransactionGuid: props.entity,
                errorMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.RefundLoadError' }),
            });
            setRefundModal(true);
        } else if (actionItem.code === 'ChangeCancellationDate') {
            props.doReduceCancellationResult(null);
            setNewCancellationDate(props.policySummary?.effectiveDate || null);
            setChangeCancellationDateModal(true);
        } else if (actionItem.code === 'CancellationQuote') {
            props.doQuoteCancellation({
                policyTransactionGuid: props.entity,
                successMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationQuoteSuccess' }),
                errorMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationQuoteError' }),
            });
        } else if (actionItem.code === 'DeleteCancellation') {
            setDeleteCancellationModal(true);
        } else if (actionItem.code === 'AcceptReferral') {
            setAction(actionItem);
            props.doGetOverridableErrors();
        } else if (
            actionItem.code === 'ConfirmBind' &&
            !actionItem?.MGAProducts.some((x) => x.includes(props.policy.product))
        ) {
            setConfirmBindModal(true);
        } else if (actionItem.code === 'ConfirmBind') {
            props.doExecutePolicyAction({ code: 'ConfirmBind' });
        } else if (actionItem.code === 'UnderwriterReviewLink') {
            props.doGetUnderwriterReviewLink();
        } else if (actionItem.code === 'ContinueQuote') {
            try {
                await handleContinueQuoteClick(null, {
                    siteCode: 'corporateWeb',
                    path: 'workflow',
                    entity: props.policySummary.guid,
                    isBlankPage: true,
                });
            } catch (error) {
                console.error('Error continuing quote:', error);
            }
        } else {
            props.doExecutePolicyAction(actionItem);
        }
    };

    const handleContinueQuoteClick = async (e, payload) => {
        await getAndUseRoute(e, props.context.session, payload);
    };

    const checkPolicNoValidation = (policyNo) => {
        const regEx = /^[A-Za-z0-9.-]*$/;
        return policyNo && policyNo.length <= 20 && regEx.test(policyNo);
    };

    const handleDocumentSelect = (guid) => {
        let docs = [...selectedDocuments];
        if (docs.includes(guid)) {
            docs = docs.filter((x) => x !== guid);
        } else {
            docs.push(guid);
        }
        setSelectedDocuments(docs);
    };

    const handleIssueQuoteModalCancel = () => {
        setIssueQuoteModal(false);
        setIssuePolicyModal(false);
        setSelectedDocuments([]);
    };

    const handleIssueQuoteModalOk = () => {
        props.doExecutePolicyTransactionCustomAction({
            ccRecipients: ccRecipients,
            documentAttachmentGuids: selectedDocuments,
            sendEmailToClient: shouldProposalEmail,
            userAddedNoteForEmail: userAddedNoteForEmail,
            policyTransactionGuid: props.entity,
        });
        setIssueQuoteModal(false);
        setSelectedDocuments([]);
        setCcRecipients('');
        setUserAddedNoteForEmail('');
    };

    const handleIssuePolicyModalCancel = () => {
        setIssuePolicyModal(false);
        setSelectedDocuments([]);
    };

    const handleIssuePolicyModalOk = () => {
        props.doReduceParameters([
            {
                code: 'SendEmailToClient',
                value: shouldProposalEmail,
            },
            {
                code: 'DocumentAttachmentGuids',
                value: selectedDocuments.join('|'),
            },
        ]);

        handleClick(action).catch((error) => {
            console.error('Error handling action:', error);
        });

        handleIssuePolicyModalCancel();
    };

    const paymentLinkConfirmationCloseHandler = () => {
        setPaymentLinkConfirmationModal(false);
        window.location.reload();
    };

    const endorsementCloseHandler = () => {
        setEndorseModal(false);
        setEndorsementEffectiveDate(null);
    };

    const endorsementEffectiveDateChangeHandler = (value) => {
        setEndorsementEffectiveDate(value);
    };

    const endorsementCreateHandler = () => {
        props.doCreateEndorsement({
            sourceTransactionGuid: props.entity,
            endorsementEffectiveDate,
            successMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.EndorsementCreatedSuccess' }),
            errorMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.EndorsementCreatedError' }),
        });
    };

    const cancelModalCloseHandler = () => {
        setCancelModal(false);
        setCancellationDate(null);
        setCancellationReason(null);
        setCancellationOtherReason('');
    };

    const createCancellationHandler = () => {
        // On success the saga navigates to the new cancellation; on error it toasts and the modal stays open to retry.
        props.doCreateCancellation({
            sourceTransactionGuid: props.entity,
            cancellationDate,
            cancellationReasonCode: cancellationReason.code,
            cancellationReasonDescription: cancellationReason.code === 'OTHER' ? cancellationOtherReason : '',
            successMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationCreatedSuccess' }),
            errorMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationCreatedError' }),
        });
    };

    const changeCancellationDateCloseHandler = () => {
        setChangeCancellationDateModal(false);
        setNewCancellationDate(null);
    };

    const updateCancellationDateHandler = () => {
        props.doUpdateCancellationDate({
            policyTransactionGuid: props.entity,
            cancellationDate: newCancellationDate,
            successMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationDateUpdateSuccess' }),
            errorMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationDateUpdateError' }),
        });
    };

    const deleteCancellationHandler = () => {
        props.doDeleteCancellation({
            policyTransactionGuid: props.entity,
            successMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationDeleteSuccess' }),
            errorMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationDeleteError' }),
        });
        setDeleteCancellationModal(false);
    };

    // Until the cancellation loads there is no known amount - never treat that as a zero refund.
    const isCancellationLoaded = props.cancellation !== null && props.cancellation !== undefined;
    // Override ?? Calculated - the amount currently held on the cancellation.
    const effectiveRefundAmount = isCancellationLoaded
        ? (props.cancellation.effectiveRefundAmount ?? props.cancellation.calculatedRefundAmount ?? 0)
        : null;
    // Methods follow what the user has typed so they appear as soon as an amount is entered,
    // falling back to the amount already on the cancellation when the override box is empty.
    const typedOverrideAmount = refundOverrideAmount === '' ? null : Number(refundOverrideAmount);
    // The refund is always entered as a positive amount - the backend applies the sign.
    const isOverrideAmountValid =
        typedOverrideAmount !== null && !Number.isNaN(typedOverrideAmount) && typedOverrideAmount >= 0;
    const pendingRefundAmount = isOverrideAmountValid ? typedOverrideAmount : effectiveRefundAmount;
    const requiresRefundMethod = pendingRefundAmount > 0;
    const isStripeRefund = refundMethod?.code === 'Stripe';
    // Compared in cents so float representation noise cannot leave the entered amount permanently unapplied.
    const toCents = (amount) => Math.round(amount * 100);
    // Apply is the commit step - completing while an entered amount is unapplied would use the stale amount.
    const hasUnappliedOverride =
        isOverrideAmountValid &&
        (!isCancellationLoaded || toCents(typedOverrideAmount) !== toCents(effectiveRefundAmount));

    const refundModalCloseHandler = () => {
        setRefundModal(false);
        setRefundOverrideAmount('');
        setRefundMethod(null);
        setRefundReference('');
        setRefundDate(null);
        // The override changed the refund only on the backend; refresh so the closed page reflects it.
        if (refundOverrideApplied) {
            props.doGetPolicySummary();
            props.doGetPolicy();
        }
        setRefundOverrideApplied(false);
    };

    const applyRefundOverrideHandler = () => {
        props.doOverrideCancellationRefund({
            policyTransactionGuid: props.entity,
            refundAmount: typedOverrideAmount,
            errorMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.RefundOverrideError' }),
        });
        setRefundOverrideApplied(true);
    };

    const completeRefundHandler = () => {
        // Nothing is sent for the method when no refund is due.
        props.doCompleteCancellation({
            policyTransactionGuid: props.entity,
            refundMethod: requiresRefundMethod ? refundMethod.code : null,
            refundReference: requiresRefundMethod && !isStripeRefund ? refundReference : null,
            refundDate: requiresRefundMethod && !isStripeRefund ? refundDate : null,
            successMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.RefundCompleteSuccess' }),
            errorMessage: intl.formatMessage({ id: 'Profile_Menu.Modal.RefundCompleteError' }),
        });
    };

    const sortedMenu = sortMenuByStatus(
        ActionsMenuConfig,
        props.policySummary.status,
        props.policySummary.type,
        props.context.session.userType,
    );
    const actions = getAvailableActions(sortedMenu, props.policySummary.product, props.policySummary.role) || [];
    const policyNo = props.policy?.number;
    const validPolicyNo = checkPolicNoValidation(policyNo);

    const isIndicativeQuote = props.policySummary.isIndicativeQuote;
    const isFinalizeQuote =
        (props.policySummary.status === 'quoted' || props.policySummary.status === 'quoteAccepted') &&
        isIndicativeQuote;
    const isFinalizeQuoteAction = {
        code: 'ReQuote',
        hooks: [],
    };

    const quotesCount = props.policySummary?.quotes?.length || 0;
    const hasMultipleQuotes = quotesCount > 1;

    const loading = props.context.loading;
    const apiStatus = props.context.operation?.status;
    const apiMessage = props.context.operation?.errors?.length ? props.context.operation?.errors[0].value : '';

    const renderSecondaryButton = (actionItem, text, clickEvent) => {
        let buttonText;

        if (typeof text === 'string') {
            // For hardcoded strings, use appropriate ID based on action code
            switch (actionItem.code) {
                case 'IssueQuote':
                    buttonText = intl.formatMessage({ id: 'Profile_Menu.IssueQuote' });
                    break;
                case 'Issue':
                    buttonText = intl.formatMessage({ id: 'Profile_Menu.Issue' });
                    break;
                case 'SendPaymentLink':
                    buttonText = intl.formatMessage({ id: 'Profile_Menu.SendPaymentLink' });
                    break;
                case 'ChargeCard':
                    buttonText = intl.formatMessage({ id: 'Profile_Menu.ChargeCard' });
                    break;
                case 'Endorse':
                    buttonText = intl.formatMessage({ id: 'Profile_Menu.Endorse' });
                    break;
                case 'ReQuote':
                    buttonText = intl.formatMessage({ id: 'Profile_Menu.FinalizeQuote' });
                    break;
                default:
                    buttonText = text;
            }
        } else {
            // For FormattedMessage components, keep as is
            buttonText = text;
        }

        return (
            <SecondaryButton
                key={actionItem.code}
                left={!screen?.isMobile ? sizes.xs : sizes.None}
                bottom={!screen?.isMobile ? sizes.None : sizes.xs}
                text={buttonText}
                clickEvent={clickEvent}
            />
        );
    };

    return (
        <Container>
            {
                <MenuContainer
                    top={sizes.xs}
                    bottom={sizes.xs}
                    inline={!screen?.isMobile}
                    horizontalAlignment={alignments.center}
                >
                    {actions &&
                        !isFinalizeQuote &&
                        actions.map((actionItem) => {
                            switch (actionItem.code) {
                                case 'IssueQuote':
                                    // Only show Issue Quote when status is 'quoted' if there's exactly 1 quote
                                    if (props.policySummary.status === 'quoted' && hasMultipleQuotes) {
                                        return null;
                                    }
                                    return renderSecondaryButton(actionItem, 'Issue Quote', () => {
                                        setIssueQuoteModal(true);
                                        setAction(actionItem);
                                    });
                                case 'Issue':
                                    return renderSecondaryButton(actionItem, 'Issue Policy', () => {
                                        setIssuePolicyModal(true);
                                        setAction(actionItem);
                                    });
                                case 'SendPaymentLink':
                                    if (props.policy?.allowSendPaymentLinkAction) {
                                        return renderSecondaryButton(actionItem, 'Send Payment Link', () => {
                                            props.doGetPaymentEmail();
                                            setSendPaymentLinkModal(true);
                                            setAction(actionItem);
                                        });
                                    }
                                    return null;
                                case 'ChargeCard':
                                    if (props.policy?.allowChargeCardAction) {
                                        return renderSecondaryButton(actionItem, 'Charge Card', () =>
                                            props.doChargeCard(),
                                        );
                                    }
                                    return null;
                                case 'Endorse':
                                    if (props.context?.configuration?.allowEndorsement === false) {
                                        return null;
                                    }
                                    return renderSecondaryButton(actionItem, 'Endorse', () => setEndorseModal(true));
                                default:
                                    return renderSecondaryButton(
                                        actionItem,
                                        actionItem.text ? (
                                            `${actionItem.text}`
                                        ) : (
                                            <FormattedMessage
                                                id={`Profile_ActionsMenu.${actionItem.code === 'ConfirmBind' && actionItem?.MGAProducts.some((x) => x.includes(props.policy.product)) ? 'Bind' : actionItem.code}`}
                                            />
                                        ),
                                        () => handleClick(actionItem),
                                    );
                            }
                        })}
                    {isFinalizeQuote &&
                        renderSecondaryButton(isFinalizeQuoteAction, 'Finalize Quote', () =>
                            handleClick(isFinalizeQuoteAction),
                        )}
                </MenuContainer>
            }
            {issueQuoteModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={handleIssueQuoteModalCancel}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.IssueQuote' })}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <ScrollableModalContent>
                                <CheckBox
                                    label={intl.formatMessage({ id: 'Profile_Menu.Modal.SendProposalEmail' })}
                                    value={shouldProposalEmail}
                                    clickEvent={() => setShouldProposalEmail((prev) => !prev)}
                                    bottom={sizes.sm}
                                />
                                {shouldProposalEmail &&
                                    props.document?.shareableDocuments &&
                                    props.document?.shareableDocuments.length > 0 && (
                                        <>
                                            <Label
                                                text={intl.formatMessage({
                                                    id: 'Profile_Menu.Modal.SelectDocuments',
                                                })}
                                                textAlign={alignments.left}
                                                size={sizes.lg}
                                                bottom={sizes.xs}
                                                bold
                                            />
                                            <CustomContainer>
                                                {props.document?.shareableDocuments.map((document) => (
                                                    <SpaceBetweenContainer
                                                        key={document.guid}
                                                        inline
                                                        verticalAlignment={alignments.center}
                                                    >
                                                        <CheckBox
                                                            label={document.displayName}
                                                            size={sizes.xs}
                                                            key={document.guid}
                                                            value={selectedDocuments.includes(document.guid)}
                                                            clickEvent={() => handleDocumentSelect(document.guid)}
                                                            bottom={sizes.xs}
                                                        />
                                                        <Link
                                                            text={intl.formatMessage({
                                                                id: 'Profile_Menu.Modal.Download',
                                                            })}
                                                            size={sizes.xs}
                                                            clickEvent={() =>
                                                                props.document.doGetDocument(document.guid)
                                                            }
                                                        />
                                                    </SpaceBetweenContainer>
                                                ))}
                                            </CustomContainer>
                                        </>
                                    )}
                                <Label
                                    text={intl.formatMessage({ id: 'Profile_Menu.Modal.AddCC' })}
                                    textAlign={alignments.left}
                                    size={sizes.lg}
                                    top={sizes.sm}
                                    bottom={sizes.xs}
                                    bold
                                />
                                <Input
                                    value={ccRecipients}
                                    placeholder={intl.formatMessage({ id: 'Profile_Menu.Input.EmailPlaceholder' })}
                                    changeEvent={(value) => setCcRecipients(value)}
                                    bottom={sizes.sm}
                                />
                                <Label
                                    text={intl.formatMessage({ id: 'Profile_Menu.Modal.AddNote' })}
                                    textAlign={alignments.left}
                                    size={sizes.lg}
                                    bottom={sizes.xs}
                                    bold
                                />
                                <StyledTextArea
                                    text={userAddedNoteForEmail}
                                    placeholder={intl.formatMessage({ id: 'Profile_Menu.Input.NotePlaceholder' })}
                                    changeEvent={(value) => setUserAddedNoteForEmail(value)}
                                    bottom={sizes.sm}
                                />
                                <Label
                                    text={intl.formatMessage({ id: 'Profile_Menu.Modal.EmailInfo' })}
                                    textAlign={alignments.left}
                                    size={sizes.lg}
                                    bottom={sizes.sm}
                                />
                            </ScrollableModalContent>
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            primary: false,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            disabled: false,
                            clickEvent: handleIssueQuoteModalCancel,
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Issue' }),
                            disabled: false,
                            clickEvent: handleIssueQuoteModalOk,
                        },
                    ]}
                />
            )}
            {issuePolicyModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={handleIssuePolicyModalCancel}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.IssuePolicy' })}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Container>
                                <CheckBox
                                    label={intl.formatMessage({ id: 'Profile_Menu.Modal.SendPolicyConfirmation' })}
                                    value={shouldProposalEmail}
                                    clickEvent={() => setShouldProposalEmail((prev) => !prev)}
                                    bottom={sizes.sm}
                                />
                                {shouldProposalEmail &&
                                    props.document?.shareableDocuments &&
                                    props.document?.shareableDocuments.length > 0 && (
                                        <>
                                            <Label
                                                text={intl.formatMessage({
                                                    id: 'Profile_Menu.Modal.SelectDocuments',
                                                })}
                                                textAlign={alignments.left}
                                                size={sizes.lg}
                                                bottom={sizes.xs}
                                                bold
                                            />
                                            <CustomContainer>
                                                {props.document?.shareableDocuments.map((document) => (
                                                    <SpaceBetweenContainer
                                                        key={document.guid}
                                                        inline
                                                        verticalAlignment={alignments.center}
                                                    >
                                                        <CheckBox
                                                            label={document.displayName}
                                                            size={sizes.xs}
                                                            value={selectedDocuments.includes(document.guid)}
                                                            clickEvent={() => handleDocumentSelect(document.guid)}
                                                            bottom={sizes.xs}
                                                        />
                                                        <Link
                                                            text={intl.formatMessage({
                                                                id: 'Profile_Menu.Modal.Download',
                                                            })}
                                                            size={sizes.xs}
                                                            clickEvent={() =>
                                                                props.document.doGetDocument(document.guid)
                                                            }
                                                        />
                                                    </SpaceBetweenContainer>
                                                ))}
                                            </CustomContainer>
                                        </>
                                    )}
                            </Container>
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            primary: false,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            disabled: false,
                            clickEvent: handleIssuePolicyModalCancel,
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Issue' }),
                            disabled: false,
                            clickEvent: handleIssuePolicyModalOk,
                        },
                    ]}
                />
            )}
            {lostModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={() => {
                        props.doReduceParameters([]);
                        setLostModal(false);
                    }}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.LoseLeadTitle' })}
                                size={sizes.md}
                                bottom={sizes.xs}
                            />
                            <DropDown
                                top={sizes.xs}
                                placeholder={intl.formatMessage({ id: 'Profile_Menu.Modal.LoseLeadReason' })}
                                label={intl.formatMessage({ id: 'Profile_Menu.Modal.LoseLeadQuestion' })}
                                value={{ code: props.parameters?.find((x) => x.code === 'LostReason')?.value }}
                                references={props.reference.references.LostReason}
                                referencesEvent={() => props.reference.doGetReferences('LostReason')}
                                changeEvent={(value) => {
                                    props.doReduceParameter({ code: 'LostReason', value: value.code });
                                }}
                                allowEdit
                            />
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: () => {
                                props.doReduceParameters([]);
                                setLostModal(false);
                            },
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Ok' }),
                            disabled: !props.parameters?.find((x) => x.code === 'LostReason')?.value,
                            clickEvent: () => {
                                if (props.policySummary.type === 'endorsement') {
                                    const lostReasonCode = props.parameters?.find(
                                        (x) => x.code === 'LostReason',
                                    )?.value;
                                    const lostReasonDescription = props.reference.references.LostReason?.find(
                                        (reason) => reason.code === lostReasonCode,
                                    )?.value;
                                    props.doSetEndorsementToLost({
                                        policyTransactionGuid: props.entity,
                                        lostReasonCode,
                                        lostReasonDescription,
                                        errorMessage: intl.formatMessage({
                                            id: 'Profile_Menu.Modal.EndorsementLostError',
                                        }),
                                    });
                                } else {
                                    props.doExecutePolicyAction({ code: 'Lost' });
                                }
                                setLostModal(false);
                            },
                        },
                    ]}
                />
            )}
            {rejectModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={() => {
                        props.doReduceParameters([]);
                        setRejectModal(false);
                    }}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.RejectPolicyTitle' })}
                                size={sizes.md}
                                bottom={sizes.xs}
                            />
                            <DropDown
                                top={sizes.xs}
                                placeholder={intl.formatMessage({ id: 'Profile_Menu.Modal.RejectPolicyReason' })}
                                label={intl.formatMessage({ id: 'Profile_Menu.Modal.RejectPolicyQuestion' })}
                                value={{
                                    code: props.parameters?.find((x) => x.code === 'RejectedReason')?.value || false,
                                }}
                                references={props.reference.references.RejectedReason}
                                referencesEvent={() => props.reference.doGetReferences('RejectedReason')}
                                changeEvent={(value) => {
                                    props.doReduceParameter({ code: 'RejectedReason', value: value.code });
                                }}
                                allowEdit
                            />
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: () => {
                                props.doReduceParameters([]);
                                setRejectModal(false);
                            },
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Ok' }),
                            disabled: !props.parameters?.find((x) => x.code === 'RejectedReason')?.value,
                            clickEvent: () => {
                                props.doExecutePolicyAction({ code: 'RejectSubmission' });
                                setRejectModal(false);
                            },
                        },
                    ]}
                />
            )}
            {rejectReferralModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={() => {
                        setRejectReferralModal(false);
                        setRejectReferralReason('');
                    }}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.RejectReferralTitle' })}
                                size={sizes.md}
                                bottom={sizes.xs}
                            />
                            <Input
                                top={sizes.xs}
                                label={intl.formatMessage({ id: 'Profile_Menu.Modal.RejectReferralQuestion' })}
                                placeholder={intl.formatMessage({ id: 'Profile_Menu.Modal.RejectReferralReason' })}
                                value={rejectReferralReason}
                                changeEvent={(value) => setRejectReferralReason(value)}
                                allowEdit
                            />
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: () => {
                                setRejectReferralModal(false);
                                setRejectReferralReason('');
                            },
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Ok' }),
                            disabled: !rejectReferralReason || rejectReferralReason.trim() === '',
                            clickEvent: () => {
                                props.doReduceParameter({ code: 'DeclinedReason', value: rejectReferralReason });
                                props.doExecutePolicyAction({ code: 'DECLINE' });
                                setRejectReferralModal(false);
                                setRejectReferralReason('');
                            },
                        },
                    ]}
                />
            )}
            {acceptReferralModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={() => {
                        setAcceptReferralModal(false);
                        setAction(null);
                    }}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.AcceptReferralTitle' })}
                                size={sizes.md}
                                top={sizes.xs}
                                bottom={sizes.md}
                            />
                            <Label
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.AcceptReferralOverridableErrors' })}
                                textAlign={alignments.left}
                                size={sizes.lg}
                                top={sizes.md}
                                bottom={sizes.sm}
                                bold
                            />
                            <BulletList
                                size={sizes.md}
                                references={props.overridableErrors.map((error, index) => ({
                                    code: error.fieldCode || `error-${index}`,
                                    value: error.errorValue,
                                }))}
                                bottom={sizes.sm}
                            />
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: () => {
                                setAcceptReferralModal(false);
                                setAction(null);
                            },
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Ok' }),
                            clickEvent: () => {
                                props.doExecutePolicyAction(action);
                                setAcceptReferralModal(false);
                                setAction(null);
                            },
                        },
                    ]}
                />
            )}
            {confirmBindModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={() => {
                        props.doReduceParameters([]);
                        setConfirmBindModal(false);
                    }}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.ConfirmBind' })}
                                size={sizes.md}
                                bottom={sizes.xs}
                            />
                            <Input
                                label={intl.formatMessage({ id: 'Profile_Menu.Modal.EnterPolicyNumber' })}
                                value={policyNo ? policyNo : ''}
                                changeEvent={(value) => {
                                    const isValid = checkPolicNoValidation(value) || value.length === 0;
                                    if (isValid) {
                                        props.doReducePolicyElement({ code: 'number', value: value });
                                    }
                                }}
                                error={!validPolicyNo}
                                allowEdit
                            />
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: () => {
                                props.doReduceParameters([]);
                                setConfirmBindModal(false);
                            },
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Ok' }),
                            disabled: !validPolicyNo,
                            clickEvent: () => {
                                props.doExecutePolicyAction({ code: 'ConfirmBind' });
                                setConfirmBindModal(false);
                                window.location.reload();
                            },
                        },
                    ]}
                />
            )}
            {sendPaymentLinkModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={() => {
                        setSendPaymentLinkModal(false);
                    }}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkTitle' })}
                                size={sizes.md}
                                textAlign={alignments.left}
                                top={sizes.md}
                                bottom={sizes.sm}
                            />
                            {loading ? (
                                <Container fullWidth horizontalAlignment={alignments.center}>
                                    <Spinner size={sizes.xl} />
                                </Container>
                            ) : props.sendPaymentLinkDto?.pageSettings?.isPaymentReceiveWindowClosed ? (
                                <>
                                    <Label
                                        text={intl.formatMessage({
                                            id: 'Profile_Menu.Modal.PaymentLinkWindowClosedInfo1',
                                        })}
                                        textAlign={alignments.left}
                                        size={sizes.md}
                                        bottom={sizes.sm}
                                    />
                                    <Label
                                        text={intl.formatMessage({
                                            id: 'Profile_Menu.Modal.PaymentLinkWindowClosedInfo2',
                                        })}
                                        textAlign={alignments.left}
                                        size={sizes.md}
                                        bottom={sizes.sm}
                                    />
                                </>
                            ) : (
                                <>
                                    <Label
                                        text={intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkInfo1' })}
                                        textAlign={alignments.left}
                                        size={sizes.lg}
                                        bottom={sizes.sm}
                                        bold
                                    />
                                    <Label
                                        text={intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkInfo2' })}
                                        textAlign={alignments.left}
                                        size={sizes.md}
                                        bottom={sizes.sm}
                                    />
                                    <Label
                                        text={intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkConfirm' })}
                                        textAlign={alignments.left}
                                        size={sizes.md}
                                        bottom={sizes.sm}
                                    />
                                    <Input
                                        value={props.paymentEmail}
                                        changeEvent={props.doReducePaymentEmail}
                                        type='email'
                                        bottom={sizes.md}
                                        allowEdit
                                    />
                                    {props.sendPaymentLinkDto?.paymentLink && (
                                        <>
                                            <Label
                                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkLabel' })}
                                                textAlign={alignments.left}
                                                size={sizes.sm}
                                                bottom={sizes.xs}
                                            />
                                            <PaymentLinkRow>
                                                <PaymentLinkLabelWrapper>
                                                    <Label
                                                        text={props.sendPaymentLinkDto.paymentLink}
                                                        textAlign={alignments.left}
                                                        size={sizes.md}
                                                    />
                                                </PaymentLinkLabelWrapper>
                                                <CopyToClipboard value={props.sendPaymentLinkDto.paymentLink} />
                                            </PaymentLinkRow>
                                        </>
                                    )}
                                </>
                            )}
                        </React.Fragment>
                    }
                    actionButtons={
                        loading
                            ? []
                            : props.sendPaymentLinkDto?.pageSettings?.isPaymentReceiveWindowClosed
                              ? [
                                    {
                                        text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                                        clickEvent: () => {
                                            setSendPaymentLinkModal(false);
                                        },
                                    },
                                ]
                              : [
                                    {
                                        text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                                        clickEvent: () => {
                                            setSendPaymentLinkModal(false);
                                        },
                                    },
                                    {
                                        primary: true,
                                        text: intl.formatMessage({ id: 'Profile_Menu.Modal.Confirm' }),
                                        disabled: loading,
                                        clickEvent: () => {
                                            props.doSendPaymentLink();
                                            setSendPaymentLinkModal(false);
                                            setPaymentLinkConfirmationModal(true);
                                        },
                                    },
                                ]
                    }
                />
            )}
            {paymentLinkConfirmationModal && !loading && (
                <GenericModal
                    animate
                    visible
                    closeEvent={paymentLinkConfirmationCloseHandler}
                    items={
                        <>
                            <Header
                                text={
                                    apiStatus
                                        ? intl.formatMessage({ id: 'Profile_Menu.Modal.SuccessTitle' })
                                        : intl.formatMessage({ id: 'Profile_Menu.Modal.ErrorTitle' })
                                }
                                size={sizes.md}
                                textAlign={alignments.left}
                                top={sizes.md}
                                bottom={sizes.sm}
                            />
                            <Label
                                text={
                                    apiStatus
                                        ? intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkSuccessMessage' })
                                        : intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkErrorMessage' })
                                }
                                textAlign={alignments.left}
                                size={sizes.lg}
                                bottom={sizes.sm}
                                bold
                            />
                            <Label
                                text={
                                    apiStatus
                                        ? intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkSuccessInfo' })
                                        : apiMessage
                                }
                                textAlign={alignments.left}
                                size={sizes.md}
                                bottom={sizes.sm}
                            />
                            {apiStatus && (
                                <Label
                                    text={intl.formatMessage({ id: 'Profile_Menu.Modal.PaymentLinkFollowUp' })}
                                    textAlign={alignments.left}
                                    size={sizes.md}
                                    bottom={sizes.md}
                                />
                            )}
                        </>
                    }
                    actionButtons={[
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Ok' }),
                            clickEvent: paymentLinkConfirmationCloseHandler,
                        },
                    ]}
                />
            )}
            {endorseModal && !loading && !props.endorsementResult?.operation?.status && (
                <GenericModal
                    animate
                    visible
                    closeEvent={endorsementCloseHandler}
                    items={
                        props.context.loading ? (
                            <Container
                                top={sizes.md}
                                verticalAlignment={alignments.center}
                                horizontalAlignment={alignments.center}
                            >
                                <Spinner size={sizes.xl} />
                            </Container>
                        ) : (
                            <>
                                <Header
                                    text={intl.formatMessage({ id: 'Profile_Menu.Modal.EndorsementTitle' })}
                                    size={sizes.md}
                                    bottom={sizes.md}
                                />
                                <Label
                                    text={intl.formatMessage({ id: 'Profile_Menu.Modal.EndorsementInfo' })}
                                    size={sizes.sm}
                                    bottom={sizes.md}
                                />
                                <Label
                                    text={intl.formatMessage({ id: 'Profile_Menu.Modal.EffectiveDate' })}
                                    textAlign={alignments.left}
                                    size={sizes.xs}
                                    bottom={sizes.xs}
                                />
                                <CustomDatePicker
                                    mode={'future'}
                                    value={endorsementEffectiveDate}
                                    locale={props.context.culture}
                                    onChange={endorsementEffectiveDateChangeHandler}
                                    placeholder={intl.formatMessage({ id: 'Profile_Menu.Modal.EffectiveDate' })}
                                    bottom={sizes.lg}
                                />
                            </>
                        )
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: endorsementCloseHandler,
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Create' }),
                            disabled: !endorsementEffectiveDate,
                            clickEvent: endorsementCreateHandler,
                        },
                    ]}
                />
            )}
            {cancelModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={cancelModalCloseHandler}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.CancelPolicyTitle' })}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Label
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationDate' })}
                                textAlign={alignments.left}
                                bottom={sizes.xs}
                            />
                            <CustomDatePicker
                                mode={'future'}
                                value={cancellationDate}
                                locale={props.context.culture}
                                onChange={setCancellationDate}
                                placeholder={intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationDate' })}
                                bottom={sizes.md}
                            />
                            <DropDown
                                placeholder={intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationReason' })}
                                label={intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationReasonQuestion' })}
                                value={{ code: cancellationReason?.code }}
                                references={props.reference.references.CancellationReason}
                                referencesEvent={() => props.reference.doGetReferences('CancellationReason')}
                                changeEvent={(value) => setCancellationReason(value)}
                                bottom={sizes.md}
                                allowEdit
                            />
                            {cancellationReason?.code === 'OTHER' && (
                                <React.Fragment>
                                    <Label
                                        text={intl.formatMessage({
                                            id: 'Profile_Menu.Modal.CancellationOtherReason',
                                        })}
                                        textAlign={alignments.left}
                                        bottom={sizes.xs}
                                    />
                                    <StyledTextArea
                                        text={cancellationOtherReason}
                                        placeholder={intl.formatMessage({
                                            id: 'Profile_Menu.Modal.CancellationOtherReasonPlaceholder',
                                        })}
                                        changeEvent={(value) => setCancellationOtherReason(value)}
                                    />
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: cancelModalCloseHandler,
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Submit' }),
                            disabled:
                                loading ||
                                !cancellationDate ||
                                !cancellationReason?.code ||
                                (cancellationReason?.code === 'OTHER' && !cancellationOtherReason.trim()),
                            clickEvent: createCancellationHandler,
                        },
                    ]}
                />
            )}
            {changeCancellationDateModal && !loading && !props.cancellationResult?.operation?.status && (
                <GenericModal
                    animate
                    visible
                    closeEvent={changeCancellationDateCloseHandler}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.ChangeCancellationDateTitle' })}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Label
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationDate' })}
                                textAlign={alignments.left}
                                bottom={sizes.xs}
                            />
                            <CustomDatePicker
                                mode={'future'}
                                value={newCancellationDate}
                                locale={props.context.culture}
                                onChange={setNewCancellationDate}
                                placeholder={intl.formatMessage({ id: 'Profile_Menu.Modal.CancellationDate' })}
                                bottom={sizes.md}
                            />
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: changeCancellationDateCloseHandler,
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Update' }),
                            disabled: loading || !newCancellationDate,
                            clickEvent: updateCancellationDateHandler,
                        },
                    ]}
                />
            )}
            {deleteCancellationModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={() => setDeleteCancellationModal(false)}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.DeleteCancellationTitle' })}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Label
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.DeleteCancellationConfirmation' })}
                                textAlign={alignments.left}
                            />
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: () => setDeleteCancellationModal(false),
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Delete' }),
                            disabled: loading,
                            clickEvent: deleteCancellationHandler,
                        },
                    ]}
                />
            )}
            {refundModal && !loading && !props.cancellationResult?.operation?.status && (
                <GenericModal
                    animate
                    visible
                    closeEvent={refundModalCloseHandler}
                    items={
                        <React.Fragment>
                            <Header
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.RefundTitle' })}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            {isCancellationLoaded && (
                                <Label
                                    text={
                                        <FormattedMessage
                                            id={'Profile_Menu.Modal.RefundAmount'}
                                            values={{
                                                amount: getFormattedCurrency(
                                                    effectiveRefundAmount,
                                                    'CAD',
                                                    props.context.session.culture,
                                                ),
                                            }}
                                        />
                                    }
                                    textAlign={alignments.center}
                                    bold
                                    bottom={sizes.md}
                                />
                            )}
                            <Label
                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.RefundOverrideAmount' })}
                                textAlign={alignments.left}
                                bottom={sizes.xs}
                            />
                            <RefundOverrideRow inline bottom={sizes.md}>
                                <Input
                                    value={refundOverrideAmount}
                                    placeholder={intl.formatMessage({
                                        id: 'Profile_Menu.Modal.RefundOverridePlaceholder',
                                    })}
                                    changeEvent={(value) => setRefundOverrideAmount(value)}
                                    type={'money'}
                                    symbolLabel={'$'}
                                    symbolLabelAlign={intl.locale?.startsWith('fr') ? 'right' : 'left'}
                                    allowEdit
                                />
                                <SecondaryButton
                                    left={sizes.xs}
                                    text={intl.formatMessage({ id: 'Profile_Menu.Modal.Apply' })}
                                    disabled={loading || !isCancellationLoaded || !isOverrideAmountValid}
                                    clickEvent={applyRefundOverrideHandler}
                                />
                            </RefundOverrideRow>
                            {requiresRefundMethod && (
                                <React.Fragment>
                                    <DropDown
                                        placeholder={intl.formatMessage({
                                            id: 'Profile_Menu.Modal.RefundMethodPlaceholder',
                                        })}
                                        label={intl.formatMessage({ id: 'Profile_Menu.Modal.RefundMethod' })}
                                        value={{ code: refundMethod?.code }}
                                        references={props.reference.references[REFUND_METHOD_REFERENCE]}
                                        referencesEvent={() =>
                                            props.reference.doGetReferences({
                                                code: REFUND_METHOD_REFERENCE,
                                                custom: true,
                                            })
                                        }
                                        changeEvent={(value) => setRefundMethod(value)}
                                        bottom={sizes.md}
                                        allowEdit
                                    />
                                    {refundMethod?.code && !isStripeRefund && (
                                        <React.Fragment>
                                            <Input
                                                label={intl.formatMessage({
                                                    id: 'Profile_Menu.Modal.RefundReference',
                                                })}
                                                placeholder={intl.formatMessage({
                                                    id: 'Profile_Menu.Modal.RefundReferencePlaceholder',
                                                })}
                                                value={refundReference}
                                                changeEvent={(value) => setRefundReference(value)}
                                                bottom={sizes.md}
                                                allowEdit
                                            />
                                            <Label
                                                text={intl.formatMessage({ id: 'Profile_Menu.Modal.RefundDate' })}
                                                textAlign={alignments.left}
                                                bottom={sizes.xs}
                                            />
                                            <CustomDatePicker
                                                value={refundDate}
                                                locale={props.context.culture}
                                                onChange={setRefundDate}
                                                placeholder={intl.formatMessage({
                                                    id: 'Profile_Menu.Modal.RefundDate',
                                                })}
                                                bottom={sizes.md}
                                            />
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.Cancel' }),
                            clickEvent: refundModalCloseHandler,
                        },
                        {
                            primary: true,
                            text: intl.formatMessage({ id: 'Profile_Menu.Modal.CompleteRefund' }),
                            disabled:
                                loading ||
                                !isCancellationLoaded ||
                                hasUnappliedOverride ||
                                (requiresRefundMethod &&
                                    (!refundMethod?.code ||
                                        (!isStripeRefund && (!refundReference.trim() || !refundDate)))),
                            clickEvent: completeRefundHandler,
                        },
                    ]}
                />
            )}
        </Container>
    );
};

export default ProfileMenu;
